import React, { useState } from 'react';
import { RegistrationFormData } from '../types/registration';
import { t } from '../services/translation';

interface Step3Props {
  data: Pick<RegistrationFormData,
    'mernokMuszakiTeruletek' | 'informatikaiTeruletek' | 'penzugyTeruletek' |
    'kereskedelemTeruletek' | 'adminisztracisTeruletek' | 'szakiranyuTeruletek' |
    'primaryArea' | 'experienceLevel'
  >;
  onChange: (fields: Partial<RegistrationFormData>) => void;
  onSubmit: () => void;
  onBack: () => void;
  isLoading: boolean;
}

// ===================== TERÜLETEK ADATAI =====================
export interface AreaItem {
  value: string;
  label: string;
  mappedValues?: string[];
  isHeader?: boolean;
  isSpacer?: boolean;
}

const AREA_GROUPS = [
  {
    key: 'mernokMuszakiTeruletek' as keyof Pick<RegistrationFormData,
      'mernokMuszakiTeruletek' | 'informatikaiTeruletek' | 'penzugyTeruletek' |
      'kereskedelemTeruletek' | 'adminisztracisTeruletek' | 'szakiranyuTeruletek'>,
    primaryValue: 'Mérnök, műszaki terület',
    label: 'Mérnöki, Műszaki',
    items: [
      {
        value: 'Automatizálási-, energetikai-, elektromérnök',
        label: 'Automatizálás, mechatronika, robotika',
        mappedValues: ['Automatizálási-, energetikai-, elektromérnök', 'Mechatronikai mérnök'],
      },
      {
        value: 'Építész-, építőmérnök',
        label: 'Építész-, építőmérnök',
      },
      {
        value: 'Gépészmérnök',
        label: 'Gépészmérnök',
      },
      {
        value: 'Gyártástámogatás, termelésirányítás',
        label: 'Gyártás, termelés, folyamatfejlesztés',
        mappedValues: ['Folyamatfejlesztő-, folyamatmérnök', 'Gyártástámogatás, termelésirányítás'],
      },
      {
        value: 'Környezetmérnök',
        label: 'Környezet-, egészség-, munkavédelem (EHS)',
      },
      {
        value: 'Közlekedés-, járműmérnök',
        label: 'Közlekedés-, járműmérnök',
      },
      {
        value: 'Mérnök értékesítő',
        label: 'Mérnök értékesítő',
      },
      {
        value: 'Minőségbiztosítás, minőség-ellenőrzés',
        label: 'Minőségbiztosítás',
      },
      {
        value: 'Műszaki termékfejlesztés',
        label: 'Műszaki termékfejlesztés, tesztelés, K+F',
      },
      {
        value: 'Vegyészmérnök, biomérnök',
        label: 'Vegyészmérnök, élelmiszeripari mérnök, biomérnök',
        mappedValues: ['Élelmiszeripari mérnök', 'Vegyészmérnök, biomérnök'],
      },
      {
        value: 'Villamosmérnök',
        label: 'Villamosmérnök, energetika',
      },
      {
        value: 'Műszaki rajzoló / CAD tervező',
        label: 'CAD tervező, Műszaki rajzoló',
      },
      {
        value: 'Műszaki technikus',
        label: 'Műszaki technikus',
      },
      {
        value: 'Műszaki menedzser, műszaki projektmenedzser',
        label: 'Műszaki projektmenedzser',
      },
      {
        value: 'Mérnök vezető',
        label: 'Mérnök vezető',
      },
      {
        value: 'Egyéb mérnöki terület',
        label: 'További mérnöki állások',
      },
    ] as (string | AreaItem)[],
  },
  {
    key: 'informatikaiTeruletek' as keyof Pick<RegistrationFormData,
      'mernokMuszakiTeruletek' | 'informatikaiTeruletek' | 'penzugyTeruletek' |
      'kereskedelemTeruletek' | 'adminisztracisTeruletek' | 'szakiranyuTeruletek'>,
    primaryValue: 'Informatikai terület',
    label: 'IT, Digital Technology',
    items: [
      {
        value: 'Információbiztonsági szakértő',
        label: 'AI & Cybersecurity',
      },
      {
        value: 'Adatbázis-szakértő',
        label: 'AI, adattudomány, gépi tanulás',
      },
      {
        value: 'Cloud & Infrastructure, DevOps',
        label: 'Cloud & Infrastructure, DevOps',
      },
      {
        value: 'Hardverfejlesztő',
        label: 'Hardverfejlesztés & IoT',
      },
      {
        value: 'Hálózati és rendszermérnök',
        label: 'Hálózat, telekommunikáció',
        mappedValues: ['Hálózati és rendszermérnök', 'Telekommunikációs szakember'],
      },
      {
        value: 'Informatikai értékesítő',
        label: 'Informatikai értékesítő',
      },
      {
        value: 'IT tanácsadó, elemző, auditor',
        label: 'IT Business Analyst, IT Auditor, IT üzleti elemző',
      },
      {
        value: 'IT support, helpdesk',
        label: 'IT Support, Helpdesk',
      },
      {
        value: 'No-code developer',
        label: 'No-code developer',
      },
      {
        value: 'Rendszerintegrátor, rendszertervező',
        label: 'Rendszerintegrátor, rendszertervező, Scrum Master',
        mappedValues: ['Rendszerintegrátor, rendszertervező', 'Rendszergazda, rendszerüzemeltető'],
      },
      {
        value: 'Programozó, szoftverfejlesztő',
        label: 'Szoftverfejlesztő és alkalmazáskészítő',
      },
      {
        value: 'Tesztmérnök, tesztelő',
        label: 'Tesztmérnök, tesztelő',
      },
      {
        value: 'Vállalatirányítási rendszerek, SAP',
        label: 'Üzleti megoldások, vállalatirányítási rendszerek (ERP, SAP, CRM)',
      },
      {
        value: 'IT projektmenedzser',
        label: 'IT projektmenedzsment',
      },
      {
        value: 'IT, fejlesztési vezető',
        label: 'Product Owner, CTO',
      },
      {
        value: 'Egyéb informatikai terület',
        label: 'További informatikai állások',
      },
    ] as (string | AreaItem)[],
  },
  {
    key: 'penzugyTeruletek' as keyof Pick<RegistrationFormData,
      'mernokMuszakiTeruletek' | 'informatikaiTeruletek' | 'penzugyTeruletek' |
      'kereskedelemTeruletek' | 'adminisztracisTeruletek' | 'szakiranyuTeruletek'>,
    primaryValue: 'Pénzügy, Számvitel, Logisztika terület',
    label: 'Pénzügy, Számvitel, Beszerzés, Logisztika',
    items: [
      { isHeader: true, value: '', label: 'PÉNZÜGY, SZÁMVITEL:' },
      {
        value: 'Accounting, Billing, Finance Support',
        label: 'Accounting, Billing, Finance Support',
        mappedValues: ['Accounting, Billing, Finance Support', 'Accounting, billing, finance support', 'Könyvelés, számlázás, pénzügy támogatása'],
      },
      {
        value: 'Befektetési tanácsadó, banki/biztosítási szakértő',
        label: 'Bank, biztosítás, befektetés',
      },
      {
        value: 'Kontrolling',
        label: 'Kontrolling',
      },
      {
        value: 'Könyvelés',
        label: 'Könyvelés',
      },
      {
        value: 'Adótanácsadás',
        label: 'Könyvvizsgáló, adótanácsadó',
        mappedValues: ['Adótanácsadás', 'Könyvvizsgálat, belső ellenőr'],
      },
      {
        value: 'Pénzügyi asszisztens, munkatárs',
        label: 'Pénzügy, számvitel, számlakezelés',
        mappedValues: ['Pénzügyi asszisztens, munkatárs', 'Számvitel, számlakezelés'],
      },
      {
        value: 'Gazdasági / pénzügyi / üzleti elemző',
        label: 'Pénzügyi / üzleti elemző, BI Analyst',
      },
      {
        value: 'Treasury, kockázatkezelés',
        label: 'Risk & Treasury',
      },
      {
        value: 'Pénzügyi, számviteli vezető',
        label: 'Pénzügyi, számviteli vezető, projektmenedzsment',
      },
      { isHeader: true, value: '', label: 'BESZERZÉS, LOGISZTIKA, SUPPLY CHAIN:' },
      {
        value: 'Beszerzés',
        label: 'Beszerzés',
      },
      {
        value: 'Logisztika, szállítmányozás, fuvarszervezés, vámügyintéző',
        label: 'Logisztika, szállítmányozás, fuvarszervezés, vámügyintézés',
      },
      {
        value: 'Logistic Support',
        label: 'Logistic Support',
      },
      {
        value: 'Raktározás, készletezés, Supply Chain',
        label: 'Raktározás, készletezés, Supply Chain',
      },
      {
        value: 'Szállítmányozási, logisztikai vezető',
        label: 'Beszerzési, szállítmányozási, logisztikai vezető',
      },
      {
        value: 'További gazdasági állások',
        label: 'További gazdasági állások',
        isSpacer: true,
      },
    ] as (string | AreaItem)[],
  },
  {
    key: 'kereskedelemTeruletek' as keyof Pick<RegistrationFormData,
      'mernokMuszakiTeruletek' | 'informatikaiTeruletek' | 'penzugyTeruletek' |
      'kereskedelemTeruletek' | 'adminisztracisTeruletek' | 'szakiranyuTeruletek'>,
    primaryValue: 'Kereskedelem/Értékesítés, Marketing, HR terület',
    label: 'Sales, Retail, Marketing, HR',
    items: [
      { isHeader: true, value: '', label: 'SALES, RETAIL:' },
      {
        value: 'Termék-, szolgáltatásértékesítő',
        label: 'Account Manager, termék-, szolgáltatásértékesítő',
      },
      {
        value: 'Customer Success',
        label: 'Customer Success',
      },
      {
        value: 'Telefonos értékesítő',
        label: 'Inside Sales, Telefonos értékesítő',
      },
      {
        value: 'Sales Operations & Support',
        label: 'Sales Operations & Support',
      },
      {
        value: 'Területi képviselő',
        label: 'Területi képviselő',
      },
      {
        value: 'Értékesítési/kereskedelmi vezető',
        label: 'Sales Manager',
      },
      {
        value: 'Egyéb értékesítés, kereskedelem terület',
        label: 'További sales állások',
      },
      {
        value: 'Kereskedő, eladó',
        label: 'Eladó, pénztáros, áruösszekészítő, raktáros',
      },
      {
        value: 'Üzletvezető/boltvezető',
        label: 'Retail & Store Management',
      },
      { isHeader: true, value: '', label: 'MARKETING:' },
      {
        value: 'Márka-, termékmenedzser',
        label: 'Brand & Product Management',
      },
      {
        value: 'PR, kommunikációs munkatárs, szövegíró',
        label: 'Copywriting & Communications, PR',
      },
      {
        value: 'Kreatív munkatárs, grafikus, webdesigner',
        label: 'Design & Creative (UI/UX, Visual)',
      },
      {
        value: 'Rendezvényszervezés',
        label: 'Event & Community Management',
      },
      {
        value: 'Marketing Analytics & Insights',
        label: 'Marketing Analytics & Insights',
      },
      {
        value: 'Performance & PPC Marketing',
        label: 'Performance & PPC Marketing',
      },
      {
        value: 'Social Media, Content & Influencer Marketing',
        label: 'Social Media, Content & Influencer Marketing',
      },
      {
        value: 'Marketing vezető',
        label: 'Marketing (Project) Manager & Coordinator',
        mappedValues: ['Marketing vezető', 'Projektmenedzsment'],
      },
      {
        value: 'Egyéb marketing terület',
        label: 'További marketing állások',
      },
      { isHeader: true, value: '', label: 'HR:' },
      {
        value: 'HR (szervezetfejlesztés, képzésszervezés)',
        label: 'Employer Branding & Culture',
      },
      {
        value: 'HR (bérszámfejtés, munkaügy)',
        label: 'HR Operations & Payroll',
      },
      {
        value: 'HR Support',
        label: 'HR Support',
      },
      {
        value: 'HR (toborzás, kiválasztás)',
        label: 'Recruitment & Talent Acquisition',
      },
      {
        value: 'HR vezető',
        label: 'HR Generalist, HR Manager',
      },
      {
        value: 'Egyéb HR terület',
        label: 'További HR állások',
      },
    ] as (string | AreaItem)[],
  },
  {
    key: 'adminisztracisTeruletek' as keyof Pick<RegistrationFormData,
      'mernokMuszakiTeruletek' | 'informatikaiTeruletek' | 'penzugyTeruletek' |
      'kereskedelemTeruletek' | 'adminisztracisTeruletek' | 'szakiranyuTeruletek'>,
    primaryValue: 'Adminisztráció, Ügyfélszolgálat terület',
    label: 'Adminisztráció, Ügyfélszolgálat',
    items: [
      {
        value: 'Adminisztrátor, adatrögzítő, dokumentumkezelő',
        label: 'Adminisztráció, adatrögzítés',
      },
      {
        value: 'Irodavezető, back office munkatárs',
        label: 'Irodavezetés, back office',
      },
      {
        value: 'Recepciós',
        label: 'Recepció',
      },
      {
        value: 'Online ügyfélszolgálat',
        label: 'Online ügyfélszolgálat',
      },
      {
        value: 'Személyes ügyfélszolgálat, ügyfélreferens, fiókhálózati munkatárs',
        label: 'Személyes ügyfélszolgálat',
      },
      {
        value: 'Személyi asszisztens',
        label: 'Személyi, szakmai, projektasszisztens',
      },
      {
        value: 'Telefonos ügyfélszolgálat, call center operátor',
        label: 'Telefonos ügyfélszolgálat, Call Center operátor',
      },
      {
        value: 'Vevőszolgálat',
        label: 'Vevőszolgálat',
      },
      {
        value: 'Ügyfélszolgálati vezető',
        label: 'Ügyfélszolgálati vezető, csoportvezető',
      },
      {
        value: 'További admin, ügyfélszolgálati állások',
        label: 'További admin, ügyfélszolgálati állások',
      },
    ] as (string | AreaItem)[],
  },
  {
    key: 'szakiranyuTeruletek' as keyof Pick<RegistrationFormData,
      'mernokMuszakiTeruletek' | 'informatikaiTeruletek' | 'penzugyTeruletek' |
      'kereskedelemTeruletek' | 'adminisztracisTeruletek' | 'szakiranyuTeruletek'>,
    primaryValue: 'Szakirányú végzettséget igénylő további terület',
    label: 'Szakirányú végzettséget igénylő további területek',
    items: [
      {
        value: 'EGÉSZSÉGÜGY, GYÓGYSZERIPAR:',
        label: 'EGÉSZSÉGÜGY, GYÓGYSZERIPAR:',
        isHeader: true,
      },
      {
        value: 'Ápoló, szakápoló, betegszállító',
        label: 'Ápoló, szakápoló, betegszállító',
      },
      {
        value: 'Egészségügyi technikus, (szak)asszisztens, laboráns',
        label: 'Egészségügyi technikus, (szak)asszisztens, laboráns',
      },
      {
        value: 'Gyógyszeripari kutató, fejlesztő',
        label: 'Gyógyszeripari kutató, fejlesztő',
      },
      {
        value: 'Orvos- és patikalátogató',
        label: 'Orvos- és patikalátogató',
      },
      {
        value: 'Orvos, szakorvos, gyógyszerész',
        label: 'Orvos, szakorvos, gyógyszerész',
      },
      {
        value: 'Államigazgatás, közigazgatás',
        label: 'Államigazgatás, közigazgatás, pályázatírás',
        isSpacer: true,
      },
      {
        value: 'Fordítás, tolmácsolás',
        label: 'Fordítás, tolmácsolás',
      },
      {
        value: 'Jogtudományok',
        label: 'Jogtudományok',
      },
      {
        value: 'Képzés, oktatás',
        label: 'Képzés, oktatás, coaching',
      },
      {
        value: 'Biológus, kémikus',
        label: 'Természet- és élettudományok',
        mappedValues: ['Biológus, kémikus', 'Fizikus, matematikus'],
      },
      {
        value: 'Éttermi vendéglátás',
        label: 'Éttermi vendéglátás',
      },
      {
        value: 'Turizmus, idegenforgalom',
        label: 'Turizmus, idegenforgalom',
      },
      {
        value: 'Anyagmozgatás, rakodás',
        label: 'Anyagmozgatás, rakodás',
      },
      {
        value: 'Gépjárművezető, sofőr, futár',
        label: 'Gépjárművezető, sofőr, futár',
      },
      {
        value: 'További, felsőfokú végzettséget igénylő állások',
        label: 'További, felsőfokú végzettséget igénylő állások',
        isSpacer: true,
      },
      {
        value: 'További, középfokú végzettséget igénylő állások',
        label: 'További, középfokú végzettséget igénylő állások',
      },
    ] as (string | AreaItem)[],
  },
];

const EXPERIENCE_OPTIONS = [
  { value: 'Diákmunka, szakmai gyakorlat', label: 'Diákmunka, szakmai gyakorlat' },
  { value: 'Maximum 1 év tapasztalat', label: 'Maximum 1 év tapasztalat' },
  { value: '2-3 év tapasztalat', label: '2-3 év tapasztalat' },
  { value: '4-7 év tapasztalat', label: '4-7 év tapasztalat' },
  { value: 'Minimum 8 év tapasztalat', label: 'Minimum 8 év tapasztalat' },
];

export const getDbValuesForGroup = (groupKey: string, selectedLabels: string[]): string[] => {
  const group = AREA_GROUPS.find(g => g.key === groupKey);
  if (!group) return selectedLabels;

  const dbValues: string[] = [];
  selectedLabels.forEach(labelOrValue => {
    const item = group.items.find(
      i => typeof i !== 'string' && (i.label === labelOrValue || i.value === labelOrValue)
    );
    if (item && typeof item !== 'string') {
      if (!dbValues.includes(item.value)) {
        dbValues.push(item.value);
      }
    } else {
      if (!dbValues.includes(labelOrValue)) {
        dbValues.push(labelOrValue);
      }
    }
  });

  return dbValues;
};

// ===================== ACCORDION =====================
interface AccordionProps {
  groupKey: string;
  label: string;
  items: (string | AreaItem)[];
  selected: string[];
  onToggle: (item: string | AreaItem) => void;
}

const Accordion: React.FC<AccordionProps> = ({ groupKey, label, items, selected, onToggle }) => {
  const [open, setOpen] = useState(false);
  const count = selected.length;

  return (
    <div className={`jw-accordion${open ? ' jw-open' : ''}${count > 0 ? ' has-selection' : ''}`}>
      <div
        className="jw-accordion-header"
        onClick={() => setOpen(v => !v)}
        role="button"
        aria-expanded={open}
        tabIndex={0}
        onKeyDown={e => e.key === 'Enter' || e.key === ' ' ? setOpen(v => !v) : undefined}
      >
        <span className="jw-accordion-title">
          {t(label)}
          {count > 0 && <span className="jw-accordion-badge">{count}</span>}
        </span>
        <span className="jw-accordion-arrow">▾</span>
      </div>
      {open && (
        <div className="jw-accordion-body">
          <div className="jw-checkbox-group">
            {items.map((item, idx) => {
              if (typeof item !== 'string' && item.isHeader) {
                return (
                  <div
                    key={`header-${idx}`}
                    className="jw-checkbox-subheader"
                    style={{
                      fontWeight: 700,
                      fontSize: '12px',
                      textTransform: 'uppercase',
                      color: '#925bff',
                      marginTop: idx > 0 ? '14px' : '4px',
                      marginBottom: '6px',
                      paddingLeft: '2px',
                      letterSpacing: '0.5px'
                    }}
                  >
                    {t(item.label)}
                  </div>
                );
              }

              const displayLabel = typeof item === 'string' ? item : item.label;
              const itemKey = typeof item === 'string' ? item : item.label;
              const isSpacer = typeof item !== 'string' && item.isSpacer;
              const isChecked = selected.includes(itemKey);

              return (
                <label
                  key={`${groupKey}-${itemKey}-${idx}`}
                  className={`jw-checkbox-label${isChecked ? ' selected' : ''}`}
                  style={isSpacer ? { marginTop: '16px' } : undefined}
                >
                  <input
                    type="checkbox"
                    checked={isChecked}
                    onChange={() => onToggle(item)}
                    id={`${groupKey}-${itemKey}-${idx}`}
                  />
                  <span>{t(displayLabel)}</span>
                </label>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

// ===================== MAIN COMPONENT =====================
const Step3ProfessionalData: React.FC<Step3Props> = ({
  data, onChange, onSubmit, onBack, isLoading
}) => {
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [touched, setTouched] = useState<Record<string, boolean>>({});

  // Összes kijelölt terület
  const totalSelected = [
    ...data.mernokMuszakiTeruletek,
    ...data.informatikaiTeruletek,
    ...data.penzugyTeruletek,
    ...data.kereskedelemTeruletek,
    ...data.adminisztracisTeruletek,
    ...data.szakiranyuTeruletek,
  ].length;

  // Csak azok a főkategóriák jelenjenek meg az elsődleges terület mezőben, ahol legalább 1 elem ki van jelölve
  const activePrimaryGroups = AREA_GROUPS.filter(g => (data[g.key] as string[]).length > 0);
  const primaryOptions = activePrimaryGroups.map(g => ({ value: g.primaryValue, label: g.label }));

  const handleToggle = (groupKey: keyof typeof data, item: string | AreaItem) => {
    const current = data[groupKey] as string[];
    const itemKey = typeof item === 'string' ? item : item.label;

    const isChecked = current.includes(itemKey);

    let updated: string[];
    if (isChecked) {
      updated = current.filter(i => i !== itemKey);
    } else {
      updated = [...current, itemKey];
    }
    
    // Számítsuk ki az aktív csoportokat a módosítás után
    const nextData = { ...data, [groupKey]: updated };
    const nextActiveGroups = AREA_GROUPS.filter(g => (nextData[g.key] as string[]).length > 0);
    const nextActiveValues = nextActiveGroups.map(g => g.primaryValue);

    let nextPrimaryArea = data.primaryArea;
    if (nextActiveGroups.length === 1) {
      nextPrimaryArea = nextActiveGroups[0].primaryValue;
    } else if (!nextActiveValues.includes(data.primaryArea)) {
      nextPrimaryArea = '';
    }

    onChange({
      [groupKey]: updated,
      primaryArea: nextPrimaryArea,
    } as Partial<RegistrationFormData>);
  };

  const validate = (): boolean => {
    const e: Record<string, string> = {};
    if (totalSelected === 0) e.areas = t('Legalább egy területet kötelező megjelölni.');
    if (!data.primaryArea) e.primaryArea = t('Elsődleges terület megadása kötelező.');
    if (!data.experienceLevel) e.experienceLevel = t('Tapasztalati szint megadása kötelező.');
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = () => {
    setTouched({ areas: true, primaryArea: true, experienceLevel: true });
    if (validate()) onSubmit();
  };

  return (
    <div className="jw-step-enter" id="jw-reg-step-3">
      <h2 className="jw-step-heading">{t('Szakmai adatok')}</h2>

      {/* TERÜLETEK */}
      <div className="jw-field">
        <div className="jw-section-label">
          {t('Milyen területen keresel állást?')} <span style={{ color: 'var(--color-primary)' }}>*</span>
        </div>
        <div className="jw-section-sublabel">
          {t('Legalább egy területet kötelező megjelölni. Lenyithatod a kategóriákat és azon belül kiválaszthatod a számodra releváns szakirányokat.')}
        </div>

        {AREA_GROUPS.map(group => (
          <Accordion
            key={group.key}
            groupKey={group.key}
            label={group.label}
            items={group.items}
            selected={data[group.key] as string[]}
            onToggle={item => handleToggle(group.key, item)}
          />
        ))}

        {touched.areas && errors.areas && (
          <div className="jw-input-error" style={{ marginTop: 8 }}>⚠ {errors.areas}</div>
        )}
        {totalSelected > 0 && (
          <div style={{ fontSize: 12, color: 'var(--color-primary)', marginTop: 6, fontWeight: 600 }}>
            ✓ {totalSelected} {t('terület kijelölve')}
          </div>
        )}
      </div>

      {/* ELSŐDLEGES TERÜLET */}
      <div className="jw-field">
        <div className="jw-section-label">
          {t('Elsődleges érdeklődési területed')} <span style={{ color: 'var(--color-primary)' }}>*</span>
        </div>
        <div className="jw-section-sublabel">
          {t('Válaszd ki azt a főterületet, amelyen leginkább állást keresel.')}
        </div>
        {primaryOptions.length === 0 ? (
          <div style={{ fontSize: 13, color: '#666', fontStyle: 'italic', marginTop: 8 }}>
            {t('Először jelölj ki legalább egy területet a fenti kategóriákból.')}
          </div>
        ) : (
          <div className="jw-radio-group" role="radiogroup" aria-label={t('Elsődleges terület')}>
            {primaryOptions.map(opt => (
              <label
                key={opt.value}
                className={`jw-radio-label${data.primaryArea === opt.value ? ' selected' : ''}`}
              >
                <input
                  type="radio"
                  name="primaryArea"
                  value={opt.value}
                  checked={data.primaryArea === opt.value}
                  onChange={() => {
                    onChange({ primaryArea: opt.value });
                    setTouched(t => ({ ...t, primaryArea: true }));
                  }}
                />
                <span>{t(opt.label)}</span>
              </label>
            ))}
          </div>
        )}
        {touched.primaryArea && errors.primaryArea && (
          <div className="jw-input-error">⚠ {errors.primaryArea}</div>
        )}
      </div>

      {/* TAPASZTALAT */}
      <div className="jw-field">
        <div className="jw-section-label">
          {t('Szakmai tapasztalat szintje')} <span style={{ color: 'var(--color-primary)' }}>*</span>
        </div>
        <div className="jw-radio-group" role="radiogroup" aria-label={t('Tapasztalati szint')}>
          {EXPERIENCE_OPTIONS.map(opt => (
            <label
              key={opt.value}
              className={`jw-radio-label${data.experienceLevel === opt.value ? ' selected' : ''}`}
            >
              <input
                type="radio"
                name="experienceLevel"
                value={opt.value}
                checked={data.experienceLevel === opt.value}
                onChange={() => {
                  onChange({ experienceLevel: opt.value });
                  setTouched(t => ({ ...t, experienceLevel: true }));
                }}
              />
              <span>{t(opt.label)}</span>
            </label>
          ))}
        </div>
        {touched.experienceLevel && errors.experienceLevel && (
          <div className="jw-input-error">⚠ {errors.experienceLevel}</div>
        )}
      </div>

      <div className="jw-required-info">{t('A kötelező mezőket * karakter jelöli.')}</div>

      <div className="jw-btn-row">
        <button
          type="button"
          className="jw-btn jw-btn-secondary"
          onClick={onBack}
          disabled={isLoading}
          id="reg-step3-back"
        >
          ← {t('Vissza')}
        </button>
        <button
          type="button"
          className="jw-btn jw-btn-primary"
          onClick={handleSubmit}
          disabled={isLoading}
          id="reg-step3-submit"
        >
          {isLoading ? (
            <>
              <span className="jw-btn-spinner" />
              {t('Regisztráció...')}
            </>
          ) : (
            t('REGISZTRÁCIÓ BEFEJEZÉSE')
          )}
        </button>
      </div>
    </div>
  );
};

export default Step3ProfessionalData;
